import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ShoppingCartRoundedIcon from '@mui/icons-material/ShoppingCartRounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import PublicLayout from '../components/PublicLayout.jsx';
import { resolveShopContext } from '../common/shopContext';
import { getSiteTheme, normalizeTemplateKey } from '../common/websiteTemplates';
import { addToCart, loadCart } from '../common/shopUtils';
import { formatMoney, getActiveCampaigns, getEffectiveProductPricing, isWithinSchedule } from '../common/shopPricing';
import { isPartnerComingSoon, isPartnerMaintenance } from '../common/partnerUtils';
import PartnerUpsellPage from './PartnerUpsellPage';
import PartnerMaintenancePage from './PartnerMaintenancePage';
import PartnerComingSoonPage from './PartnerComingSoonPage';
import { getLocalizedShopItem } from '../common/shopContent';

const formatDateShort = (value) => {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(new Date(value));
  } catch (e) {
    return String(value);
  }
};

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const stripHtml = (value) => String(value || '').replace(/<[^>]*>/g, '').trim();

const getCoverImage = (product) => {
  if (!product) return '';
  const direct = product.image || product.thumbnail || '';
  if (direct) return direct;
  const gallery = Array.isArray(product.images) ? product.images.filter(Boolean) : [];
  return gallery[0] || '';
};

const isServiceItem = (product) => product?.type === 'service';

const isOutOfStockItem = (product) => {
  if (!product) return false;
  if (isServiceItem(product)) return false;
  if (product.stock === undefined || product.stock === null || product.stock === '') return false;
  const stock = Number(product.stock || 0);
  return Number.isFinite(stock) && stock <= 0;
};

const getCartCount = (scopeKey) => {
  const cart = loadCart(scopeKey);
  return Object.values(cart || {}).reduce((sum, qty) => sum + Math.max(0, Number(qty || 0)), 0);
};

export default function Shop() {
  const navigate = useNavigate();
  const location = useLocation();
  const { partnerSlug } = useParams();
  const auth = useSelector((state) => state.auth);
  const settings = useSelector((state) => state.settingsdata.settings);
  const { t, i18n } = useTranslation();

  const shopContext = useMemo(
    () =>
      resolveShopContext(settings, {
        partnerSlug,
        locale: i18n.language,
        pathname: location.pathname,
      }),
    [settings, partnerSlug, i18n.language, location.pathname]
  );

  const { partner, cartScopeKey, paths, websiteContent, prefixPartnerLink } = shopContext;
  const isAdmin = auth?.profile?.usertype === 'admin' || auth?.profile?.usertype === 'fleetadmin';

  const templateKey = normalizeTemplateKey(websiteContent.template || 'classic');
  const theme = getSiteTheme(templateKey, websiteContent.appearance || {});

  const shop = websiteContent.shop || {};
  const currency = shop.currency || 'USD';
  const shopEnabled = shop.enabled !== false;

  const campaigns = useMemo(() => (Array.isArray(shop.campaigns) ? shop.campaigns : []), [shop.campaigns]);
  const activeCampaigns = useMemo(() => getActiveCampaigns(campaigns), [campaigns]);

  const products = useMemo(() => {
    const list = Array.isArray(shop.products) ? shop.products.filter((p) => p && p.published !== false) : [];
    return list.map((p) => getLocalizedShopItem(p, i18n.language));
  }, [shop.products, i18n.language]);

  const subscriptions = useMemo(() => {
    const list = Array.isArray(shop.subscriptions) ? shop.subscriptions.filter((p) => p && p.published !== false) : [];
    return list.map((p) => getLocalizedShopItem(p, i18n.language));
  }, [shop.subscriptions, i18n.language]);

  const auctions = useMemo(() => {
    const list = Array.isArray(shop.auctions) ? shop.auctions.filter((p) => p && p.published !== false) : [];
    return list.map((p) => getLocalizedShopItem(p, i18n.language));
  }, [shop.auctions, i18n.language]);

  const catalogCategories = useMemo(() => {
    const list = Array.isArray(shop.categories) ? shop.categories : [];
    return list
      .filter((cat) => cat && cat.published !== false && cat.id)
      .map((cat) => getLocalizedShopItem(cat, i18n.language));
  }, [shop.categories, i18n.language]);

  const banners = useMemo(() => {
    const nowMs = Date.now();
    const list = Array.isArray(shop.banners) ? shop.banners : [];
    return list.filter((b) => b && b.active !== false && isWithinSchedule(b.startsAt, b.endsAt, nowMs));
  }, [shop.banners]);

  const [bannerIndex, setBannerIndex] = useState(0);
  useEffect(() => {
    if (banners.length <= 1) return;
    const id = window.setInterval(() => setBannerIndex((idx) => (idx + 1) % banners.length), 7500);
    return () => window.clearInterval(id);
  }, [banners.length]);
  useEffect(() => {
    if (bannerIndex >= banners.length) setBannerIndex(0);
  }, [bannerIndex, banners.length]);

  const pricedProducts = useMemo(() => {
    return products.map((product) => ({
      product,
      pricing: getEffectiveProductPricing(product, campaigns),
    }));
  }, [products, campaigns]);

  const dealProducts = useMemo(() => {
    const deals = pricedProducts.filter((item) => Boolean(item.pricing?.campaign?.id));
    deals.sort((a, b) => Number(b.pricing?.campaign?.discountPercent || 0) - Number(a.pricing?.campaign?.discountPercent || 0));
    return deals.slice(0, 10);
  }, [pricedProducts]);

  const categoryDefs = useMemo(() => {
    const discovered = new Set();
    products.forEach((p) => {
      if (p?.category) discovered.add(String(p.category));
    });

    const labels = new Map();
    catalogCategories.forEach((cat) => {
      const key = String(cat?.id || '').trim();
      if (!key) return;
      labels.set(key, cat?.title || key);
    });

    const orderedKeys = [];
    catalogCategories.forEach((cat) => {
      const key = String(cat?.id || '').trim();
      if (key && discovered.has(key)) orderedKeys.push(key);
    });

    Array.from(discovered)
      .sort((a, b) => a.localeCompare(b))
      .forEach((key) => {
        if (!orderedKeys.includes(key)) orderedKeys.push(key);
      });

    return orderedKeys.map((key) => ({ key, label: labels.get(key) || key }));
  }, [products, catalogCategories]);

  const categoryKeys = useMemo(() => categoryDefs.map((cat) => cat.key), [categoryDefs]);

  const categoryCards = useMemo(() => {
    const counts = new Map();
    products.forEach((p) => {
      const key = String(p?.category || '').trim();
      if (!key) return;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return catalogCategories
      .map((cat) => ({
        ...cat,
        _count: counts.get(String(cat.id)) || 0,
      }))
      .filter((cat) => cat._count > 0)
      .sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
  }, [catalogCategories, products]);

  const typeCounts = useMemo(() => {
    const service = products.filter((p) => p?.type === 'service').length;
    const product = products.length - service;
    return { service, product, total: products.length };
  }, [products]);

  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all'); // all | product | service
  const [category, setCategory] = useState('all');
  const [sort, setSort] = useState('featured'); // featured | price_low | price_high
  useEffect(() => {
    if (category === 'all') return;
    if (!categoryKeys.includes(category)) setCategory('all');
  }, [categoryKeys, category]);

  const filteredProducts = useMemo(() => {
    const q = normalizeText(query);
    return pricedProducts.filter(({ product }) => {
      const type = product?.type === 'service' ? 'service' : 'product';
      if (typeFilter !== 'all' && type !== typeFilter) return false;
      if (category !== 'all' && String(product.category || '') !== category) return false;
      if (!q) return true;
      const haystack = [
        product.title,
        product.description,
        product.category,
        product.tags,
        product.id,
        product.slug,
      ]
        .filter(Boolean)
        .map(normalizeText)
        .join(' ');
      return haystack.includes(q);
    });
  }, [category, pricedProducts, query, typeFilter]);

  const sortedProducts = useMemo(() => {
    const next = [...filteredProducts];
    if (sort === 'featured') {
      next.sort((a, b) => {
        const af = a.product?.featured ? 1 : 0;
        const bf = b.product?.featured ? 1 : 0;
        if (bf !== af) return bf - af;
        return String(a.product?.title || '').localeCompare(String(b.product?.title || ''));
      });
    }
    if (sort === 'price_low') next.sort((a, b) => Number(a.pricing.finalPrice || 0) - Number(b.pricing.finalPrice || 0));
    if (sort === 'price_high') next.sort((a, b) => Number(b.pricing.finalPrice || 0) - Number(a.pricing.finalPrice || 0));
    return next;
  }, [filteredProducts, sort]);

  const [cartCount, setCartCount] = useState(() => getCartCount(cartScopeKey));

  const [snack, setSnack] = useState({ open: false, message: '' });

  if (partner?.inactive) {
    return <PartnerUpsellPage partner={partner} />;
  }

  if (partner && isPartnerComingSoon(partner) && !isAdmin) {
    return <PartnerComingSoonPage partner={partner} />;
  }

  if (partner && isPartnerMaintenance(partner) && !isAdmin) {
    return <PartnerMaintenancePage partner={partner} />;
  }

  const followLink = (link) => {
    if (!link) return;
    const href = String(link);
    if (href.startsWith('http://') || href.startsWith('https://')) {
      window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(prefixPartnerLink(href));
  };

  const quickAdd = (event, product) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!product?.id) return;
    if (isOutOfStockItem(product)) {
      setSnack({ open: true, message: t('out_of_stock') || 'Out of stock' });
      return;
    }
    addToCart(product.id, 1, cartScopeKey);
    setCartCount(getCartCount(cartScopeKey));
    setSnack({ open: true, message: t('added_to_cart') || 'Added to cart' });
  };

  const renderBanner = () => {
    if (!banners.length) {
      return (
        <Box
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            border: `1px solid ${theme.border}`,
            bgcolor: theme.darkSectionBg || '#0b0b0b',
            color: theme.darkSectionText || '#fff',
            px: { xs: 3, md: 5 },
            py: { xs: 4, md: 5 },
          }}
        >
          <Typography sx={{ fontWeight: 950, fontSize: { xs: 30, md: 40 }, letterSpacing: -1 }}>
            {t('shop') || 'Shop'}
          </Typography>
          <Typography sx={{ color: theme.darkSectionMuted || 'rgba(255,255,255,0.75)', mt: 1, maxWidth: 640 }}>
            {t('shop_subtitle') || 'Official Plukk products, passes, and perks.'}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3 }}>
            <Button
              variant="contained"
              onClick={() => document.getElementById('shop-products')?.scrollIntoView({ behavior: 'smooth' })}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900, bgcolor: theme.accent || '#16a34a' }}
            >
              {t('browse_products') || 'Browse products'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate(paths.cart)}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900, borderColor: 'rgba(255,255,255,0.25)', color: '#fff' }}
              startIcon={<ShoppingCartRoundedIcon />}
            >
              {(t('cart') || 'Cart') + (cartCount ? ` (${cartCount})` : '')}
            </Button>
          </Stack>
        </Box>
      );
    }

    const canNav = banners.length > 1;

    return (
      <Box
        sx={{
          position: 'relative',
          borderRadius: 4,
          overflow: 'hidden',
          border: `1px solid ${theme.border}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            transform: `translateX(-${bannerIndex * 100}%)`,
            transition: 'transform 600ms cubic-bezier(0.2, 0.8, 0.2, 1)',
          }}
        >
          {banners.map((banner) => {
            const hasImage = Boolean(banner.image);
            const title = banner.title || (t('shop') || 'Shop');
            const subtitle = banner.subtitle || (t('shop_subtitle') || 'Official Plukk products, passes, and perks.');
            const buttonText = banner.buttonText || t('view') || 'View';
            const href = banner.link || paths.shop;

            return (
              <Box
                key={banner.id || title}
                sx={{
                  flex: '0 0 100%',
                  minHeight: { xs: 240, md: 300 },
                  position: 'relative',
                  cursor: href ? 'pointer' : 'default',
                  backgroundColor: theme.darkSectionBg || '#0b0b0b',
                  backgroundImage: hasImage ? `url(${banner.image})` : undefined,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
                onClick={() => followLink(href)}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background: hasImage
                      ? 'linear-gradient(90deg, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.32) 70%, rgba(0,0,0,0.25) 100%)'
                      : 'radial-gradient(900px 360px at 25% 30%, rgba(56,189,248,0.35), transparent 60%), radial-gradient(900px 360px at 80% 30%, rgba(34,197,94,0.25), transparent 60%), linear-gradient(135deg, #0b1120 0%, #111827 60%)',
                  }}
                />
                <Box sx={{ position: 'relative', px: { xs: 3, md: 5 }, py: { xs: 4, md: 5 }, color: '#fff' }}>
                  <Stack spacing={1} sx={{ maxWidth: 640 }}>
                    <Typography sx={{ fontWeight: 950, fontSize: { xs: 30, md: 40 }, letterSpacing: -1 }}>
                      {title}
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.78)', fontSize: 16, lineHeight: 1.6 }}>
                      {subtitle}
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 1.5 }}>
                      <Button
                        variant="contained"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          followLink(href);
                        }}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 900,
                          bgcolor: theme.accent || '#16a34a',
                          '&:hover': { bgcolor: theme.accent || '#15803d' },
                        }}
                      >
                        {buttonText}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate(paths.cart);
                        }}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 900,
                          borderColor: 'rgba(255,255,255,0.25)',
                          color: '#fff',
                          '&:hover': { borderColor: 'rgba(255,255,255,0.4)', bgcolor: 'rgba(255,255,255,0.08)' },
                        }}
                        startIcon={<ShoppingCartRoundedIcon />}
                      >
                        {(t('cart') || 'Cart') + (cartCount ? ` (${cartCount})` : '')}
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              </Box>
            );
          })}
        </Box>

        {canNav ? (
          <>
            <IconButton
              onClick={() => setBannerIndex((idx) => (idx - 1 + banners.length) % banners.length)}
              sx={{
                position: 'absolute',
                top: '50%',
                left: 12,
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(0,0,0,0.35)',
                color: '#fff',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
              }}
              aria-label={t('previous') || 'Previous'}
            >
              <ChevronLeftRoundedIcon />
            </IconButton>
            <IconButton
              onClick={() => setBannerIndex((idx) => (idx + 1) % banners.length)}
              sx={{
                position: 'absolute',
                top: '50%',
                right: 12,
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(0,0,0,0.35)',
                color: '#fff',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
              }}
              aria-label={t('next') || 'Next'}
            >
              <ChevronRightRoundedIcon />
            </IconButton>
            <Stack direction="row" spacing={1} sx={{ position: 'absolute', left: 16, bottom: 16 }}>
              {banners.map((_, idx) => (
                <Box
                  key={idx}
                  onClick={() => setBannerIndex(idx)}
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '999px',
                    border: '1px solid rgba(255,255,255,0.6)',
                    bgcolor: idx === bannerIndex ? '#fff' : 'rgba(255,255,255,0.12)',
                    cursor: 'pointer',
                  }}
                />
              ))}
            </Stack>
          </>
        ) : null}
      </Box>
    );
  };

  return (
    <PublicLayout
      title={t('shop') || 'Shop'}
      partner={partner}
      homePath={paths.home}
      bookingPath={paths.booking}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 4 }, py: { xs: 5, md: 7 } }}>
        {!shopEnabled ? (
          <Box sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.border}`, bgcolor: theme.sectionAltBg }}>
            <Typography sx={{ fontWeight: 900, color: theme.text }}>
              {t('shop_disabled') || 'Shop is disabled'}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.muted, mt: 0.5 }}>
              {t('shop_disabled_help') || 'Please check back later.'}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={3.5}>
            {renderBanner()}

            {categoryCards.length ? (
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Stack>
                    <Typography sx={{ fontWeight: 950, color: theme.text, fontSize: 20 }}>
                      {t('categories') || 'Categories'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.muted }}>
                      {t('categories_help') || 'Browse curated collections tailored to your needs.'}
                    </Typography>
                  </Stack>
                  <Button
                    variant="outlined"
                    onClick={() => setCategory('all')}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
                  >
                    {t('view_all') || 'View all'}
                  </Button>
                </Stack>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                    gap: 2,
                  }}
                >
                  {categoryCards.slice(0, 6).map((cat) => (
                    <Card
                      key={cat.id}
                      variant="outlined"
                      sx={{
                        borderRadius: 4,
                        borderColor: theme.border,
                        bgcolor: theme.cardBg,
                        overflow: 'hidden',
                      }}
                    >
                      <CardActionArea onClick={() => navigate(prefixPartnerLink(`/shop/category/${encodeURIComponent(cat.id)}`))}>
                        <Box
                          sx={{
                            height: 170,
                            position: 'relative',
                            bgcolor: theme.sectionAltBg,
                            overflow: 'hidden',
                          }}
                        >
                          {cat.image ? (
                            <Box
                              component="img"
                              src={cat.image}
                              alt={cat.title}
                              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <Box
                              sx={{
                                position: 'absolute',
                                inset: 0,
                                background:
                                  'radial-gradient(900px 260px at 20% 20%, rgba(56,189,248,0.25), transparent 60%), radial-gradient(800px 260px at 80% 30%, rgba(34,197,94,0.20), transparent 60%), linear-gradient(135deg, #0b1120 0%, #111827 60%)',
                              }}
                            />
                          )}
                          <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.35)' }} />
                          <Box sx={{ position: 'absolute', bottom: 16, left: 16, right: 16, color: '#fff' }}>
                            <Typography sx={{ fontWeight: 950, fontSize: 18 }}>{cat.title || cat.id}</Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', mt: 0.5 }}>
                              {cat.description ? stripHtml(cat.description).slice(0, 90) : ''}
                            </Typography>
                          </Box>
                          <Chip
                            label={`${cat._count} ${t('items') || 'Items'}`}
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 12,
                              left: 12,
                              fontWeight: 900,
                              bgcolor: theme.accent || '#111827',
                              color: '#fff',
                            }}
                          />
                        </Box>
                        <CardContent>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography sx={{ fontWeight: 900, color: theme.text }}>
                              {t('explore') || 'Explore'}
                            </Typography>
                            <Typography sx={{ color: theme.muted }}>
                              {t('view_category') || 'View category'}
                            </Typography>
                          </Stack>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  ))}
                </Box>
              </Box>
            ) : null}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
              <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
                {subscriptions.length ? (
                  <Button
                    variant="outlined"
                    onClick={() => navigate(paths.subscriptions)}
                    startIcon={<WorkspacePremiumRoundedIcon />}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
                  >
                    {t('subscriptions') || 'Subscriptions'}
                  </Button>
                ) : null}
                {auctions.length ? (
                  <Button
                    variant="outlined"
                    onClick={() => navigate(paths.auctions)}
                    startIcon={<GavelRoundedIcon />}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
                  >
                    {t('auctions') || 'Auctions'}
                  </Button>
                ) : null}
                {cartCount ? (
                  <Button
                    variant="contained"
                    onClick={() => navigate(paths.cart)}
                    startIcon={<ShoppingCartRoundedIcon />}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 900,
                      bgcolor: theme.accent || '#111827',
                      '&:hover': { bgcolor: theme.accent || '#0b1224' },
                    }}
                  >
                    {(t('cart') || 'Cart') + ` (${cartCount})`}
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    onClick={() => navigate(paths.cart)}
                    startIcon={<ShoppingCartRoundedIcon />}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
                  >
                    {t('cart') || 'Cart'}
                  </Button>
                )}
              </Stack>

              <Box sx={{ flex: 1 }} />

              <TextField
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('search_products') || 'Search products…'}
                size="small"
                sx={{ minWidth: { xs: '100%', md: 340 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                select
                label={t('sort') || 'Sort'}
                size="small"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                sx={{ minWidth: { xs: '100%', md: 200 } }}
              >
                <MenuItem value="featured">{t('featured') || 'Featured'}</MenuItem>
                <MenuItem value="price_low">{t('price_low_high') || 'Price: low → high'}</MenuItem>
                <MenuItem value="price_high">{t('price_high_low') || 'Price: high → low'}</MenuItem>
              </TextField>
            </Stack>

            {typeCounts.service ? (
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                <Chip
                  label={(t('all') || 'All') + ` (${typeCounts.total})`}
                  onClick={() => setTypeFilter('all')}
                  variant={typeFilter === 'all' ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 900 }}
                />
                <Chip
                  label={(t('products') || 'Products') + ` (${typeCounts.product})`}
                  onClick={() => setTypeFilter('product')}
                  variant={typeFilter === 'product' ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 900 }}
                />
                <Chip
                  label={(t('services') || 'Services') + ` (${typeCounts.service})`}
                  onClick={() => setTypeFilter('service')}
                  variant={typeFilter === 'service' ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 900 }}
                />
              </Stack>
            ) : null}

            {categoryDefs.length ? (
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                <Chip
                  label={t('all') || 'All'}
                  onClick={() => setCategory('all')}
                  variant={category === 'all' ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 900 }}
                />
                {categoryDefs.map((cat) => (
                  <Chip
                    key={cat.key}
                    label={cat.label}
                    onClick={() => setCategory(cat.key)}
                    variant={category === cat.key ? 'filled' : 'outlined'}
                    sx={{ fontWeight: 800 }}
                  />
                ))}
              </Stack>
            ) : null}

            {dealProducts.length ? (
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LocalOfferRoundedIcon fontSize="small" />
                    <Typography sx={{ fontWeight: 950, color: theme.text, fontSize: 18 }}>
                      {t('deals') || 'Deals'}
                    </Typography>
                  </Stack>
                  {activeCampaigns.length ? (
                    <Typography variant="body2" sx={{ color: theme.muted }}>
                      {(t('active_campaigns') || 'Active campaigns') + `: ${activeCampaigns.length}`}
                    </Typography>
                  ) : null}
                </Stack>

                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    overflowX: 'auto',
                    pb: 1,
                    scrollSnapType: 'x mandatory',
                    '&::-webkit-scrollbar': { height: 10 },
                    '&::-webkit-scrollbar-thumb': { background: theme.border, borderRadius: 999 },
                  }}
                >
                  {dealProducts.map(({ product, pricing }) => (
                    <Card
                      key={product.id}
                      variant="outlined"
                      sx={{
                        minWidth: 280,
                        maxWidth: 320,
                        borderRadius: 3,
                        scrollSnapAlign: 'start',
                        borderColor: theme.border,
                        bgcolor: theme.cardBg,
                      }}
                    >
                      <CardActionArea onClick={() => navigate(prefixPartnerLink(`/shop/${encodeURIComponent(product.id)}`))}>
                        <Box
                          sx={{
                            height: 170,
                            position: 'relative',
                            bgcolor: theme.sectionAltBg,
                            overflow: 'hidden',
                          }}
                        >
                          {getCoverImage(product) ? (
                            <Box
                              component="img"
                              src={getCoverImage(product)}
                              alt={product.title}
                              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <Box
                              sx={{
                                position: 'absolute',
                                inset: 0,
                                background:
                                  'radial-gradient(900px 260px at 20% 20%, rgba(56,189,248,0.35), transparent 60%), radial-gradient(800px 260px at 80% 30%, rgba(34,197,94,0.25), transparent 60%)',
                              }}
                            />
                          )}
                          <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.12)' }} />
                          {pricing.badgeText ? (
                            <Chip
                              label={pricing.badgeText}
                              size="small"
                              sx={{
                                position: 'absolute',
                                top: 12,
                                left: 12,
                                fontWeight: 900,
                                bgcolor: theme.accent || '#111827',
                                color: '#fff',
                              }}
                            />
                          ) : null}
                          {isServiceItem(product) ? (
                            <Chip
                              label={t('service') || 'Service'}
                              size="small"
                              sx={{
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                fontWeight: 900,
                                bgcolor: theme.cardBg,
                              }}
                              variant="outlined"
                            />
                          ) : null}
                          {isOutOfStockItem(product) ? (
                            <Chip
                              label={t('out_of_stock') || 'Out of stock'}
                              size="small"
                              sx={{
                                position: 'absolute',
                                bottom: 12,
                                left: 12,
                                fontWeight: 900,
                                bgcolor: theme.cardBg,
                              }}
                              color="warning"
                              variant="outlined"
                            />
                          ) : null}
                        </Box>
                        <CardContent>
                          <Typography sx={{ fontWeight: 950, color: theme.text, fontSize: 16 }}>
                            {product.title}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mt: 0.5 }}>
                            {pricing.compareAtPrice ? (
                              <Typography sx={{ color: theme.muted, fontWeight: 800, textDecoration: 'line-through' }}>
                                {formatMoney(pricing.compareAtPrice, currency)}
                              </Typography>
                            ) : null}
                            <Typography sx={{ fontWeight: 950, color: theme.text }}>
                              {formatMoney(pricing.finalPrice, currency)}
                            </Typography>
                          </Stack>
                          <Button
                            variant="contained"
                            onClick={(e) => quickAdd(e, product)}
                            disabled={isOutOfStockItem(product)}
                            sx={{
                              mt: 1.5,
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 900,
                              bgcolor: theme.accent || '#111827',
                              '&:hover': { bgcolor: theme.accent || '#0b1224' },
                            }}
                          >
                            {t('add_to_cart') || 'Add to cart'}
                          </Button>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  ))}
                </Box>
              </Box>
            ) : null}

            {activeCampaigns.length ? (
              <Box>
                <Typography sx={{ fontWeight: 950, color: theme.text, fontSize: 18, mb: 1.5 }}>
                  {t('campaigns') || 'Campaigns'}
                </Typography>
                <Grid container spacing={2}>
                  {activeCampaigns.slice(0, 3).map((campaign) => (
                    <Grid key={campaign.id || campaign.title} item xs={12} md={4}>
                      <Card
                        variant="outlined"
                        sx={{
                          height: '100%',
                          borderRadius: 3,
                          borderColor: theme.border,
                          bgcolor: theme.sectionAltBg,
                        }}
                      >
                        <CardContent>
                          <Stack spacing={1}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                              <Typography sx={{ fontWeight: 950, color: theme.text, fontSize: 16 }}>
                                {campaign.title || t('campaign') || 'Campaign'}
                              </Typography>
                              {campaign.badgeText || campaign.discountPercent ? (
                                <Chip
                                  label={campaign.badgeText || `${Math.round(Number(campaign.discountPercent || 0))}% OFF`}
                                  size="small"
                                  sx={{ fontWeight: 900, bgcolor: theme.accent || '#111827', color: '#fff' }}
                                />
                              ) : null}
                            </Stack>
                            {campaign.description ? (
                              <Typography variant="body2" sx={{ color: theme.muted }}>
                                {campaign.description}
                              </Typography>
                            ) : null}
                            <Divider />
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Typography variant="body2" sx={{ color: theme.muted }}>
                                {t('items') || 'Items'}
                              </Typography>
                              <Typography sx={{ fontWeight: 900, color: theme.text }}>
                                {Array.isArray(campaign.productIds) ? campaign.productIds.length : 0}
                              </Typography>
                            </Stack>
                            {campaign.endsAt ? (
                              <Typography variant="body2" sx={{ color: theme.muted }}>
                                {(t('ends') || 'Ends') + ' ' + formatDateShort(campaign.endsAt)}
                              </Typography>
                            ) : null}
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ) : null}

            <Box id="shop-products">
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.5 }}>
                <Typography sx={{ fontWeight: 950, color: theme.text, fontSize: 18 }}>
                  {t('products') || 'Products'}
                </Typography>
                <Typography variant="body2" sx={{ color: theme.muted }}>
                  {sortedProducts.length} {t('items') || 'items'}
                </Typography>
              </Stack>

              {sortedProducts.length ? (
                <Grid container spacing={2.5}>
                  {sortedProducts.map(({ product, pricing }) => (
                    <Grid item xs={12} sm={6} md={4} key={product.id || product.title}>
                      <Card
                        variant="outlined"
                        sx={{
                          borderRadius: 3,
                          overflow: 'hidden',
                          borderColor: theme.border,
                          bgcolor: theme.cardBg,
                          height: '100%',
                        }}
                      >
                        <CardActionArea
                          onClick={() =>
                            navigate(prefixPartnerLink(`/shop/${encodeURIComponent(product.id || product.slug || product.title)}`))
                          }
                        >
                          <Box
                            sx={{
                              height: 220,
                              bgcolor: theme.sectionAltBg,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              position: 'relative',
                              overflow: 'hidden',
                            }}
                          >
                            {pricing.badgeText ? (
                              <Chip
                                label={pricing.badgeText}
                                size="small"
                                sx={{
                                  position: 'absolute',
                                  top: 14,
                                  left: 14,
                                  fontWeight: 900,
                                  bgcolor: theme.accent || '#111827',
                                  color: '#fff',
                                }}
                              />
                            ) : null}
                            {isServiceItem(product) ? (
                              <Chip
                                label={t('service') || 'Service'}
                                size="small"
                                sx={{
                                  position: 'absolute',
                                  top: 14,
                                  right: 14,
                                  fontWeight: 900,
                                  bgcolor: theme.cardBg,
                                }}
                                variant="outlined"
                              />
                            ) : null}
                            {isOutOfStockItem(product) ? (
                              <Chip
                                label={t('out_of_stock') || 'Out of stock'}
                                size="small"
                                sx={{
                                  position: 'absolute',
                                  bottom: 14,
                                  left: 14,
                                  fontWeight: 900,
                                  bgcolor: theme.cardBg,
                                }}
                                color="warning"
                                variant="outlined"
                              />
                            ) : null}
                            {getCoverImage(product) ? (
                              <Box
                                component="img"
                                src={getCoverImage(product)}
                                alt={product.title}
                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  inset: 0,
                                  background:
                                    'radial-gradient(900px 260px at 20% 20%, rgba(56,189,248,0.35), transparent 60%), radial-gradient(800px 260px at 80% 30%, rgba(34,197,94,0.25), transparent 60%)',
                                }}
                              />
                            )}
                            <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.10)' }} />
                          </Box>
                          <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
                              <Typography sx={{ fontWeight: 950, color: theme.text, fontSize: 18 }}>
                                {product.title}
                              </Typography>
                              <Stack direction="row" spacing={1} alignItems="baseline">
                                {pricing.compareAtPrice ? (
                                  <Typography
                                    sx={{
                                      color: theme.muted,
                                      fontWeight: 800,
                                      textDecoration: 'line-through',
                                      display: { xs: 'none', sm: 'block' },
                                    }}
                                  >
                                    {formatMoney(pricing.compareAtPrice, currency)}
                                  </Typography>
                                ) : null}
                                <Typography sx={{ fontWeight: 950, color: theme.text }}>
                                  {formatMoney(pricing.finalPrice, currency)}
                                </Typography>
                              </Stack>
                            </Stack>
                            {product.description ? (
                              <Typography sx={{ color: theme.muted, mt: 1, minHeight: 44 }}>
                                {stripHtml(product.description).slice(0, 140)}
                              </Typography>
                            ) : null}
                            <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
                              <Button
                                variant="contained"
                                onClick={(e) => quickAdd(e, product)}
                                disabled={isOutOfStockItem(product)}
                                sx={{
                                  borderRadius: 2,
                                  textTransform: 'none',
                                  fontWeight: 900,
                                  bgcolor: theme.accent || '#111827',
                                  '&:hover': { bgcolor: theme.accent || '#0b1224' },
                                }}
                              >
                                {t('add_to_cart') || 'Add to cart'}
                              </Button>
                              <Button
                                variant="text"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  navigate(prefixPartnerLink(`/shop/${encodeURIComponent(product.id || product.slug || product.title)}`));
                                }}
                                sx={{
                                  borderRadius: 2,
                                  textTransform: 'none',
                                  fontWeight: 900,
                                  color: theme.link,
                                }}
                              >
                                {t('view') || 'View'}
                              </Button>
                            </Stack>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.border}`, bgcolor: theme.sectionAltBg }}>
                  <Typography sx={{ fontWeight: 900, color: theme.text }}>
                    {t('no_products') || 'No products yet'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.muted, mt: 0.5 }}>
                    {t('no_products_help') || 'Ask an admin to add products in Storefront > Shop.'}
                  </Typography>
                </Box>
              )}
            </Box>
          </Stack>
        )}
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={2500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity="success"
          sx={{ borderRadius: 2, fontWeight: 800 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                setSnack((s) => ({ ...s, open: false }));
                navigate(paths.cart);
              }}
            >
              {t('view_cart') || 'View cart'}
            </Button>
          }
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </PublicLayout>
  );
}
